import React, { Fragment, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiClock, FiEdit2 } from "react-icons/fi";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Col,
  FormGroup,
  Form,
  DropdownItem,
  Collapse,
  Badge,
  Row,
  InputGroup,
  InputGroupText,
  Input,
  ListGroup,
  ListGroupItem,
  Button,
  UncontrolledTooltip,
} from "reactstrap";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import { ToastContainer } from "react-toastify";
import TableWrapper from "@/utils/TableWrapper";
import type { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import type { CategoryOption, SubcategoryRecord } from "./categoryForm";

/** A pending change's per-field diff (SCRUM-1715 review flow): `to` is the proposed value. */
type PendingDiff = Record<string, { from?: unknown; to?: unknown }>;

type LockedFieldProps = {
  fieldKey: string;
  diff: PendingDiff | null;
  unlocked: boolean;
  onUnlock: (key: string) => void;
  children: React.ReactNode;
};

function LockedField({ fieldKey, diff, unlocked, onUnlock, children }: LockedFieldProps) {
  const [hovered, setHovered] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pending = diff?.[fieldKey];
  if (!pending || unlocked) return <>{children}</>;

  let proposed
  if (pending.to == null) {
    proposed = "—"
  } else if (typeof pending.to === "boolean") {
    proposed = pending.to ? "Yes" : "No"
  } else if (Array.isArray(pending.to) && pending.to[0]?.value?.label !== undefined) {
    proposed = pending.to.map((e) => e?.value?.label || "—").join(", ")
  } else if (Array.isArray(pending.to) || typeof pending.to === "object") {
    proposed = "(complex value)"
  } else {
    proposed = String(pending.to)
  }

  const show = () => { if (timerRef.current) clearTimeout(timerRef.current); setHovered(true); };
  const hide = () => { timerRef.current = setTimeout(() => setHovered(false), 120); };

  return (
    <div style={{ position: "relative" }} onMouseEnter={show} onMouseLeave={hide}>
      <div style={{ pointerEvents: "none" }}>{children}</div>
      <div style={{
        position: "absolute",
        inset: 0,
        border: "2px solid #fde047",
        borderRadius: "0.375rem",
        background: "rgba(254, 249, 195, 0.45)",
        cursor: "not-allowed",
        zIndex: 2,
      }} />
      {hovered && (
        <div
          onMouseEnter={show}
          onMouseLeave={hide}
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            zIndex: 20,
            background: "#fef9c3",
            color: "#713f12",
            border: "1px solid #fde047",
            borderRadius: "6px",
            padding: "8px 14px",
            fontSize: "0.78rem",
            whiteSpace: "nowrap",
            boxShadow: "0 4px 14px rgba(0,0,0,0.1)",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <FiClock size={13} style={{ flexShrink: 0 }} />
          <span>Pending review &nbsp;·&nbsp; Proposed: <strong>{proposed}</strong></span>
          <button
            type="button"
            onClick={() => onUnlock(fieldKey)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: "#fde047",
              border: "1px solid #ca8a04",
              borderRadius: "4px",
              color: "#713f12",
              cursor: "pointer",
              fontSize: "0.72rem",
              fontWeight: 600,
              padding: "3px 10px",
            }}
          >
            <FiEdit2 size={11} />
            Edit
          </button>
        </div>
      )}
    </div>
  );
}

// SCRUM-1778: absent or empty = every enabled property is shown (option B).
const isFlagged = (flags, id) => !flags || flags.length === 0 || flags.includes(id);

type CheckboxRowHandler = (e: React.ChangeEvent<HTMLInputElement>, element: { key: string }) => void;

// The two Yes/No flag columns differ only in the flag list and the handler (SCRUM-2279).
const flagCell = (flags, onToggle: CheckboxRowHandler, locked: boolean) => {
  const FlagCell = ({ row }) => (
    <div>
      <center>
        <label
          className="custom-toggle"
          style={locked ? { opacity: 0.55, cursor: "not-allowed" } : {}}
        >
          <input
            checked={isFlagged(flags, row.original.key)}
            type="checkbox"
            onChange={(e) => onToggle(e, row.original)}
            disabled={locked}
          />
          <span
            className="custom-toggle-slider rounded-circle"
            data-label-off="No"
            data-label-on="Yes"
          />
        </label>
      </center>
    </div>
  );
  return FlagCell;
};

/**
 * The category / property rows are the container's state, which its JSDoc typed
 * `any[]` (SCRUM-2082 keeps that type rather than inventing a shape the screen
 * never checked); everything else is typed from what the container passes.
 */
export type UpdateCategoriesViewProps = {
  alertNode: React.ReactNode;
  permission: ReturnType<typeof useCurrentPermission>;
  categories: any[];
  openedCollapses: unknown[];
  showUpdate: boolean;
  showCategoryUpdate: boolean;
  showSubCategoryUpdate: boolean;
  category: { name: string; slug: string };
  categoryValidation: { name: boolean; slug: boolean };
  subcategory: { name: string; slug: string; properties: any[]; hostsProducts: boolean };
  subcategoryValidation: { name: boolean; slug: boolean; parentcategorie: unknown };
  labelsub?: string;
  labelslugsub?: string;
  allProperties: any[];
  subProperties: any[];
  subListing: string[] | undefined;
  subBox: string[] | undefined;
  /** the site doc's `_id` as the store holds it; only compared with `===` */
  currentSiteId?: unknown;
  currentSiteCountryCode?: string;
  entityPendingChange: { diff?: PendingDiff } | null;
  pendingChangeReady: boolean;
  unlockedFields: readonly string[];
  onUnlock: (key: string) => void;

  collapsesToggle: (collapse: unknown) => void;
  searchHandler: React.ChangeEventHandler<HTMLInputElement>;
  showUpdatingCategory: (e: React.MouseEvent, ch: CategoryOption) => void;
  showUpdatingSubCategory: (e: React.MouseEvent, sub: SubcategoryRecord) => void;
  onChangeCategory: React.ChangeEventHandler<HTMLInputElement>;
  onChangeSubcategory: React.ChangeEventHandler<HTMLInputElement>;
  onClickUpdateCategory: () => void;
  onClickUpdateSubcategory: () => void;
  closeUpdate: () => void;
  DeleteCategoryAlert: () => void;
  DeleteSubcategoryAlert: () => void;
  onClickEnable: CheckboxRowHandler;
  onClickShow: CheckboxRowHandler;
  onClickBox: CheckboxRowHandler;
};

const UpdateCategoriesView = ({

  alertNode,
  permission,
  categories,
  openedCollapses,
  showUpdate,
  showCategoryUpdate,
  showSubCategoryUpdate,
  category,
  categoryValidation,
  subcategory,
  subcategoryValidation,
  labelsub = "",
  labelslugsub = "",
  allProperties,
  subProperties,
  subListing,
  subBox,
  currentSiteId,
  currentSiteCountryCode,
  entityPendingChange,
  pendingChangeReady,
  unlockedFields,
  onUnlock,

  collapsesToggle,
  searchHandler,
  showUpdatingCategory,
  showUpdatingSubCategory,
  onChangeCategory,
  onChangeSubcategory,
  onClickUpdateCategory,
  onClickUpdateSubcategory,
  closeUpdate,
  DeleteCategoryAlert,
  DeleteSubcategoryAlert,
  onClickEnable,
  onClickShow,
  onClickBox,
}: UpdateCategoriesViewProps) => {
  const diff = entityPendingChange?.diff || null;
  const unlocked = (key) => !diff || unlockedFields.includes(key);
  const propertiesLocked = !!diff?.properties && !unlocked("properties");

  const reformattedProp = useMemo(() => {
    return allProperties.map((prop) => {
      if (typeof prop.label !== "string" && Array.isArray(prop.label) && prop.label.length > 0) {
        return { ...prop, label: prop.label.filter((l) => l.siteid === currentSiteId) };
      } else if (typeof prop.label !== "string" && Array.isArray(prop.label) && prop.label.length === 0) {
        return { ...prop, label: [{ value: { label: "" } }] };
      } else {
        return { ...prop, label: [{ value: { label: prop.label } }] };
      }
    });
  }, [allProperties, currentSiteId]);

  const columns = useMemo(
    () => [
      { accessorKey: "name", header: "Name" },
      { accessorFn: (row) => row.label?.[0]?.value?.label, header: "Label" },
      { accessorKey: "description", header: "Description" },
      { accessorKey: "type", header: "Type" },
      {
        header: "Enabled",
        cell: ({ row }) => (
          <div>
            <center>
              <label
                className="custom-toggle"
                style={propertiesLocked ? { opacity: 0.55, cursor: "not-allowed" } : {}}
              >
                <input
                  checked={subProperties.includes(row.original.key)}
                  type="checkbox"
                  onChange={(e) => onClickEnable(e, row.original)}
                  disabled={propertiesLocked}
                />
                <span
                  className="custom-toggle-slider rounded-circle"
                  data-label-off="removed"
                  data-label-on="added"
                />
              </label>
            </center>
          </div>
        ),
      },
      {
        header: "Show in Detail Box",
        cell: flagCell(subBox, onClickBox, propertiesLocked),
      },
      {
        header: "Show in Listing",
        cell: flagCell(subListing, onClickShow, propertiesLocked),
      },
    ],
    [onClickEnable, onClickBox, onClickShow, subProperties, subListing, subBox, propertiesLocked]
  );

  return (
    <>
      {/* NOTIFICATIONS */}
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      {/* ALERTS */}
      {alertNode}

      <SimpleHeader name="Updating Category" parentName="Categories" />
      <Container className="mt--6" fluid>
        <Row>
          <Col>
            <Card>
              <CardHeader>
                <Row className="align-items-center">
                  <Col xs="8">
                    <h5 className="h3 mb-0">Update Categories</h5>
                  </Col>
                  {(permission?.publish || permission?.edit) && (
                    <Col className="text-right" xs="4">
                      <Button color="default" size="sm" to="/admin/category/add" tag={Link}>
                        Add New
                      </Button>
                    </Col>
                  )}
                </Row>
              </CardHeader>

              <CardBody>
                <FormGroup className="row">
                  <Col md="8">
                    <Form className="navbar-search form-inline mr-sm-3 navbar-search-dark">
                      <FormGroup className="mb-3">
                        <InputGroup className="input-group-alternative input-group-merge">
                          <InputGroupText>
                            <i className="fas fa-search" />
                          </InputGroupText>
                          <Input placeholder="Search .." type="text" onChange={searchHandler} />
                        </InputGroup>
                      </FormGroup>
                    </Form>
                  </Col>

                  <Col md="8">
                    <div className="accordion">
                      {categories.map((category) => (
                        <Fragment key={category.value._id}>
                          <Card className="card-plain">
                            <CardHeader
                              role="tab"
                              onClick={() => collapsesToggle(category.value.subcategories)}
                              aria-expanded={openedCollapses.includes(category.value.subcategories)}
                            >
                              <h3 className="mb-0">
                                {category.value.name}
                                <Button
                                  id={`tooltip${category.value._id}`}
                                  color="secondary"
                                  size="sm"
                                  data-placement="top"
                                  className="ml-3"
                                  onClick={(e) => showUpdatingCategory(e, category)}
                                >
                                  <i className="ni ni-settings-gear-65" />
                                </Button>
                                <UncontrolledTooltip
                                  delay={1000}
                                  placement="top"
                                  offset={[0, 5]}
                                  target={`tooltip${category.value._id}`}
                                >
                                  Update Category
                                </UncontrolledTooltip>
                              </h3>
                            </CardHeader>

                            <Collapse
                              role="tabpanel"
                              isOpen={openedCollapses.includes(category.value.subcategories)}
                            >
                              {category.value.subcategories.map((sub) => (
                                <Fragment key={sub._id}>
                                  <CardBody>
                                    <div className="d-flex justify-content-between pt-1">
                                      <div>
                                        <span className="text-muted text-sm font-weight-bold">
                                          {sub.name}{" "}
                                          <Badge
                                            color="primary"
                                            className="badge-md badge-circle badge-floating border-white"
                                          >
                                            {
                                              allProperties.filter((propState) => {
                                                // SCRUM-1412: count the CURRENT SITE's country
                                                // entry, the key the editor panel reads and
                                                // writes. Subcategories carry no country_code
                                                // of their own (the catalogue is country-less,
                                                // SCRUM-1402), so keying on sub.country_code
                                                // read 0 for every record.
                                                const subPropIds =
                                                  sub.properties.filter(
                                                    (p) => p.key === currentSiteCountryCode
                                                  ).length > 0
                                                    ? sub.properties.filter(
                                                        (p) => p.key === currentSiteCountryCode
                                                      )[0].value
                                                    : [];

                                                const siteList =
                                                  sub.properties.filter(
                                                    (p) => p.key === "___CURRENT_SITE___"
                                                  ).length > 0
                                                    ? sub.properties.filter(
                                                        (p) => p.key === "___CURRENT_SITE___"
                                                      )[0].value
                                                    : subPropIds;

                                                return siteList.some((id) => propState.key === id);
                                              }).length
                                            }{" "}
                                          </Badge>
                                        </span>
                                      </div>
                                      <div className="text-right">
                                        <a
                                          onClick={(e) => showUpdatingSubCategory(e, sub)}
                                          id={`tooltip${sub._id}`}
                                        >
                                          <i className=" ni ni-settings-gear-65 px-2 " />
                                        </a>
                                        <UncontrolledTooltip
                                          delay={0}
                                          trigger="hover focus"
                                          target={`tooltip${sub._id}`}
                                          offset={[0, 5]}
                                        >
                                          Update Subcategory
                                        </UncontrolledTooltip>
                                      </div>
                                    </div>
                                  </CardBody>
                                  <DropdownItem divider />
                                </Fragment>
                              ))}
                            </Collapse>
                          </Card>
                        </Fragment>
                      ))}
                    </div>
                  </Col>
                </FormGroup>
              </CardBody>
            </Card>
          </Col>

          {/* RIGHT PANEL: Update forms */}
          {showUpdate && (
            <Col xl="5">
              <Card>
                <CardHeader>
                  <Row className="align-items-center">
                    <Col xs="8">
                      <h5 className="h3 mb-0">
                        Updating {showCategoryUpdate ? <>Category</> : <>Subcategory</>}
                      </h5>
                    </Col>
                    <Col className="text-right" xs="4">
                      <Button color="danger" size="sm" onClick={closeUpdate}>
                        close
                      </Button>
                    </Col>
                  </Row>
                </CardHeader>

                <CardBody>
                  {!pendingChangeReady ? (
                    <div className="text-center py-4 text-muted">Loading...</div>
                  ) : (
                    <ListGroup className="list my--3" flush>
                      <ListGroupItem className="px-0">
                        {/* Category */}
                        {showCategoryUpdate && (
                          <>
                            <Row className="align-items-center">
                              <div className="col ml-2 ">
                                <h4 className="mb-2">
                                  <a href="#pablo" onClick={(e) => e.preventDefault()}>
                                    Name
                                  </a>
                                </h4>
                                <LockedField fieldKey="name" diff={diff} unlocked={unlocked("name")} onUnlock={onUnlock}>
                                  <InputGroup className="mb-2">
                                    <InputGroupText>
                                      <i className="ni ni-bullet-list-67"></i>
                                    </InputGroupText>
                                    <Input
                                      placeholder="Insert New Category"
                                      type="text"
                                      name="name"
                                      value={category.name}
                                      invalid={!categoryValidation.name}
                                      onChange={onChangeCategory}
                                    />
                                  </InputGroup>
                                </LockedField>
                                {!categoryValidation.name && (
                                  <InputGroup className="invalid-feedback">Required State.</InputGroup>
                                )}
                              </div>
                            </Row>

                            <Row className="align-items-center">
                              <div className="col ml-2 ">
                                <h4 className="mb-2">
                                  <a href="#pablo" onClick={(e) => e.preventDefault()}>
                                    Slug
                                  </a>
                                </h4>
                                <InputGroup className="mb-2">
                                  <InputGroupText>
                                    <i className="ni ni-world-2"></i>
                                  </InputGroupText>
                                  <Input
                                    placeholder="Slug"
                                    type="text"
                                    name="slug"
                                    value={category.slug}
                                    onChange={onChangeCategory}
                                    invalid={!categoryValidation.slug}
                                    disabled
                                  />
                                </InputGroup>
                                {!categoryValidation.slug && (
                                  <InputGroup className="invalid-feedback">Required State.</InputGroup>
                                )}
                              </div>
                            </Row>

                            {(permission?.publish || permission?.edit) && (
                              <Row className="text-right">
                                <div className="col ml-2 ">
                                  <Button
                                    className="btn-icon"
                                    color="info"
                                    type="button"
                                    outline
                                    onClick={onClickUpdateCategory}
                                    id={`tooltip-update-category`}
                                  >
                                    <span className="btn-inner--icon">
                                      <i className="ni ni-check-bold" />
                                    </span>
                                  </Button>
                                  <UncontrolledTooltip
                                    delay={0}
                                    trigger="hover focus"
                                    target={`tooltip-update-category`}
                                  >
                                    Update
                                  </UncontrolledTooltip>

                                  <Button
                                    className="btn-icon"
                                    color="danger"
                                    type="button"
                                    id={`tooltip-delete-category`}
                                    onClick={DeleteCategoryAlert}
                                  >
                                    <span className="btn-inner--icon">
                                      <i className="fas fa-trash" />
                                    </span>
                                  </Button>
                                  <UncontrolledTooltip
                                    delay={0}
                                    trigger="hover focus"
                                    target={`tooltip-delete-category`}
                                  >
                                    Delete
                                  </UncontrolledTooltip>
                                </div>
                              </Row>
                            )}
                          </>
                        )}

                        {/* Subcategory */}
                        {showSubCategoryUpdate && (
                          <>
                            <Row className="align-items-center">
                              <div className="col ml-2 ">
                                <h4 className="mb-2">
                                  <a href="#pablo" onClick={(e) => e.preventDefault()}>
                                    Subcategory
                                  </a>
                                </h4>
                                <LockedField fieldKey="name" diff={diff} unlocked={unlocked("name")} onUnlock={onUnlock}>
                                  <InputGroup className="mb-2">
                                    <InputGroupText>
                                      <i className="ni ni-align-center"></i>
                                    </InputGroupText>
                                    <Input
                                      placeholder="Insert Subcategory"
                                      type="text"
                                      name="namesub"
                                      value={subcategory.name}
                                      invalid={!subcategoryValidation.name}
                                      onChange={onChangeSubcategory}
                                    />
                                  </InputGroup>
                                </LockedField>
                                {!subcategoryValidation.name && (
                                  <InputGroup className="invalid-feedback">Required State.</InputGroup>
                                )}
                              </div>
                            </Row>

                            <Row className="align-items-center">
                              <div className="col ml-2 ">
                                <h4 className="mb-2">
                                  <a href="#pablo" onClick={(e) => e.preventDefault()}>
                                    Slug
                                  </a>
                                </h4>
                                <InputGroup className="mb-2">
                                  <InputGroupText>
                                    <i className="ni ni-world-2"></i>
                                  </InputGroupText>
                                  <Input
                                    placeholder="Slug"
                                    type="text"
                                    name="slugsub"
                                    value={subcategory.slug}
                                    onChange={onChangeSubcategory}
                                    invalid={!subcategoryValidation.slug}
                                    disabled
                                  />
                                </InputGroup>
                              </div>
                            </Row>

                            <Row className="align-items-center">
                              <div className="col ml-2 ">
                                <h4 className="mb-2">
                                  <a href="#pablo" onClick={(e) => e.preventDefault()}>
                                    Label
                                  </a>
                                </h4>
                                <LockedField fieldKey="label" diff={diff} unlocked={unlocked("label")} onUnlock={onUnlock}>
                                  <InputGroup className="mb-2">
                                    <InputGroupText>
                                      <i className="ni ni-tag"></i>
                                    </InputGroupText>
                                    <Input
                                      placeholder="Insert a Label"
                                      type="text"
                                      name="labelsub"
                                      value={labelsub}
                                      onChange={onChangeSubcategory}
                                    />
                                  </InputGroup>
                                </LockedField>
                              </div>
                            </Row>

                            <Row className="align-items-center">
                              <div className="col ml-2 ">
                                <h4 className="mb-2">
                                  <a href="#pablo" onClick={(e) => e.preventDefault()}>
                                    Label slug
                                  </a>
                                </h4>
                                <LockedField fieldKey="label" diff={diff} unlocked={unlocked("label")} onUnlock={onUnlock}>
                                  <InputGroup className="mb-2">
                                    <InputGroupText>
                                      <i className="ni ni-tag"></i>
                                    </InputGroupText>
                                    <Input
                                      placeholder="Insert a Label Slug without spaces"
                                      type="text"
                                      name="labelslugsub"
                                      value={labelslugsub}
                                      onChange={onChangeSubcategory}
                                    />
                                  </InputGroup>
                                </LockedField>
                              </div>
                            </Row>

                            {/* SCRUM-1668: the trunk flag (SCRUM-1413 D1) */}
                            <Row className="align-items-center">
                              <div className="col ml-2 ">
                                <h4 className="mb-2">
                                  <a href="#pablo" onClick={(e) => e.preventDefault()}>
                                    Hosts products (trunk)
                                  </a>
                                </h4>
                                <LockedField fieldKey="hostsProducts" diff={diff} unlocked={unlocked("hostsProducts")} onUnlock={onUnlock}>
                                  <label className="custom-toggle mb-2">
                                    <input
                                      type="checkbox"
                                      name="hostsproductssub"
                                      checked={subcategory.hostsProducts === true}
                                      onChange={onChangeSubcategory}
                                      aria-label="Hosts products (trunk)"
                                    />
                                    <span className="custom-toggle-slider rounded-circle" />
                                  </label>
                                </LockedField>
                              </div>
                            </Row>

                            <Row className="align-items-center">
                              <div className="col ml-2 ">
                                <h4 className="mb-2">
                                  <a href="#pablo" onClick={(e) => e.preventDefault()}>
                                    Properties
                                  </a>
                                </h4>

                                {propertiesLocked && (
                                  <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    background: "#fef9c3",
                                    border: "1px solid #fde047",
                                    borderRadius: "6px",
                                    padding: "6px 12px",
                                    marginBottom: 10,
                                    fontSize: "0.78rem",
                                    color: "#713f12",
                                  }}>
                                    <FiClock size={13} style={{ flexShrink: 0 }} />
                                    <span>Properties change pending review</span>
                                    <button
                                      type="button"
                                      onClick={() => onUnlock("properties")}
                                      style={{
                                        display: "flex", alignItems: "center", gap: 4,
                                        background: "#fde047", border: "1px solid #ca8a04",
                                        borderRadius: "4px", color: "#713f12", cursor: "pointer",
                                        fontSize: "0.72rem", fontWeight: 600, padding: "3px 10px",
                                        marginLeft: "auto",
                                      }}
                                    >
                                      <FiEdit2 size={11} /> Edit
                                    </button>
                                  </div>
                                )}

                                {allProperties
                                  .filter((p) => subProperties.includes(p.key))
                                  .map((prop) => (
                                    <Badge color="success" pill key={prop.key} className="mr-1">
                                      {prop.name}
                                    </Badge>
                                  ))}

                                <TableWrapper
                                  data={reformattedProp.sort((x, y) =>
                                    x.added === y.added ? 0 : x.added ? -1 : 1
                                  )}
                                  pagesToDisplay={5}
                                  columns={columns}
                                />
                              </div>
                            </Row>

                            {(permission?.publish || permission?.edit) && (
                              <Row className="text-right">
                                <div className="col ml-2 ">
                                  <Button
                                    className="btn-icon"
                                    color="info"
                                    type="button"
                                    outline
                                    onClick={onClickUpdateSubcategory}
                                    id="tooltip-update-subcategory"
                                  >
                                    <span className="btn-inner--icon">
                                      <i className="ni ni-check-bold" />
                                    </span>
                                  </Button>
                                  <UncontrolledTooltip
                                    delay={0}
                                    trigger="hover focus"
                                    target={`tooltip-update-subcategory`}
                                  >
                                    Update
                                  </UncontrolledTooltip>

                                  <Button
                                    className="btn-icon"
                                    color="danger"
                                    type="button"
                                    onClick={DeleteSubcategoryAlert}
                                    id="tooltip-delete-subcategory"
                                  >
                                    <span className="btn-inner--icon">
                                      <i className="fas fa-trash" />
                                    </span>
                                  </Button>
                                  <UncontrolledTooltip
                                    delay={0}
                                    trigger="hover focus"
                                    target={`tooltip-delete-subcategory`}
                                  >
                                    Delete
                                  </UncontrolledTooltip>
                                </div>
                              </Row>
                            )}
                          </>
                        )}
                      </ListGroupItem>
                    </ListGroup>
                  )}
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>
      </Container>
    </>
  );
};

export default React.memo(UpdateCategoriesView);
